/**
 * context/crm/RecordAccessContext.tsx
 *
 * Resolves a single CRM record's access once, at the detail-page level, and shares
 * it with every panel on that page (details card, notes, tasks, associations,
 * activity, kanban card, ...). Avoids each panel re-deriving the rule or having
 * `canEdit` prop-drilled five levels down.
 *
 * Providers nest: an associated Company row rendered inside a Contact page should
 * be wrapped in its own <RecordAccessProvider record={company}> so it is judged on
 * its own `canEdit` (section 21 — access is evaluated per record).
 */
import React, { createContext, useContext, useMemo } from 'react';
import {
  AccessControlledRecord,
  useCrmPermissions,
} from 'hooks/useCrmPermissions';

export interface RecordAccess {
  /** Section 4: every CRM User can view every record in the org. */
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canAddNote: boolean;
  canCreateTask: boolean;
  canManageAssociations: boolean;
  /**
   * Admin/Manager always; plus the record's own Owner, who may hand their record
   * off to a teammate. Deliberately NOT the same as `canEdit`: a CRM User whose
   * access came from an assigned Task or Note has work access only (section 7),
   * so they can edit the record but cannot change who owns it.
   */
  canReassignOwner: boolean;
  /** Sections 15, 16: always available; commenting never grants edit access. */
  canComment: boolean;
  /** Convenience inverse of canEdit, for "show the read-only variant" branches. */
  isViewOnly: boolean;
}

/** Fail-closed default: visible and commentable, nothing else. */
const VIEW_ONLY_ACCESS: RecordAccess = {
  canView: true,
  canEdit: false,
  canDelete: false,
  canAddNote: false,
  canCreateTask: false,
  canManageAssociations: false,
  canReassignOwner: false,
  canComment: true,
  isViewOnly: true,
};

const RecordAccessContext = createContext<RecordAccess | null>(null);

interface RecordAccessProviderProps {
  /** The record this subtree is about. Undefined while loading -> view-only. */
  record?: AccessControlledRecord | null;
  children: React.ReactNode;
}

export const RecordAccessProvider: React.FC<RecordAccessProviderProps> = ({
  record,
  children,
}) => {
  const { canEditRecord, canReassignOwner: hasOwnerAdminRights, isOwner } =
    useCrmPermissions();

  const canEdit = canEditRecord(record);
  // Admin/Manager, or the Owner of this specific record.
  const canReassignOwner = hasOwnerAdminRights || isOwner(record);

  const value = useMemo<RecordAccess>(
    () => ({
      canView: true,
      canEdit,
      // Section 6: every modification action rides on the same edit grant.
      canDelete: canEdit,
      canAddNote: canEdit,
      canCreateTask: canEdit,
      canManageAssociations: canEdit,
      canReassignOwner,
      canComment: true,
      isViewOnly: !canEdit,
    }),
    [canEdit, canReassignOwner],
  );

  return (
    <RecordAccessContext.Provider value={value}>{children}</RecordAccessContext.Provider>
  );
};

export const useRecordAccess = (): RecordAccess => {
  const ctx = useContext(RecordAccessContext);

  if (!ctx) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        '[useRecordAccess] No <RecordAccessProvider> above this component. ' +
          'Falling back to view-only access — wrap the detail page in a provider.',
      );
    }
    return VIEW_ONLY_ACCESS;
  }

  return ctx;
};

export default RecordAccessContext;